import { Stack } from '@mui/material';

import { QuestionnaireSectionLabel, QUESTIONNAIRE_PAGE_BOX_SX } from '../../../../common';
import {
  AutoSaveList,
  EMPTY_MEDICATION_VALUES,
  MedicationFields,
  NoMedicationCheckbox,
  NoMedicationConfirmDialog,
  getMedicationRowLabel,
  mapMedicationRowToValues,
  toMedicationFieldPayload,
  useFormReadOnly,
  useNoMedicationConfirm,
} from '../../../../../common';
import { MEDICATION_FORM_SCHEMA } from './validationSchema';
import { useIntakeMedications } from './hooks';
import { useIntakeQuestionnaireLoading } from '../../../hooks';
import { INTAKE_MONTH_YEAR_DISPLAY_FORMAT } from '../../../constants';
import { FORTA_BLUE } from '../../../../../../utils/colors';

import type { IMedication, MedicationFormValues, MedicationPayload } from '../../../../../common';

export default function MedicationForm() {
  const {
    medications,
    hasNoMedications,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoMedications,
  } = useIntakeMedications();
  const isLoading = useIntakeQuestionnaireLoading();
  const readOnly = useFormReadOnly();
  const hasSavedMedications = medications.length > 0;
  const { isConfirmOpen, handleToggle, handleConfirm, closeConfirm } = useNoMedicationConfirm({
    hasSavedMedications,
    clearAll,
    setNoMedication: setNoMedications,
  });

  return (
    <>
      <QuestionnaireSectionLabel variant="h5" sx={{ mb: 1.5, color: FORTA_BLUE.primary }}>
        Medications
      </QuestionnaireSectionLabel>
      <Stack gap={2} textAlign="left" sx={QUESTIONNAIRE_PAGE_BOX_SX}>
        <NoMedicationCheckbox
          checked={hasNoMedications}
          disabled={isLoading || readOnly || isBulkSaving}
          onChange={handleToggle}
        />

        {hasNoMedications ? null : (
          <AutoSaveList<MedicationFormValues, IMedication, MedicationPayload>
            addButtonLabel="Add medication"
            disabled={isLoading || isBulkSaving}
            emptyValues={EMPTY_MEDICATION_VALUES}
            entries={medications}
            getDeleteConfirm={(row) => ({
              title: 'Confirm medication deletion?',
              message: `Are you sure you want to delete medication "${row.medication_name ?? ''}"?`,
            })}
            getRowKey={(row) => row.id}
            getRowLabel={getMedicationRowLabel}
            isSaving={isSaving}
            mapRowToValues={mapMedicationRowToValues}
            newEntryLabel="New medication"
            onCreate={create}
            onDelete={remove}
            onUpdate={update}
            requiredMessage='Add at least one medication or check "No medication taken by the client"'
            renderFields={({ formik, disabled, readOnly: fieldsReadOnly, saveField, backgroundColor }) => (
              <MedicationFields
                backgroundColor={backgroundColor}
                disabled={disabled}
                formik={formik}
                monthFormat={INTAKE_MONTH_YEAR_DISPLAY_FORMAT}
                readOnly={fieldsReadOnly}
                saveField={saveField}
              />
            )}
            toFieldPayload={toMedicationFieldPayload}
            validationSchema={MEDICATION_FORM_SCHEMA}
          />
        )}

        <NoMedicationConfirmDialog
          open={isConfirmOpen}
          hasSavedMedications={hasSavedMedications}
          confirmLoading={isBulkSaving}
          onClose={closeConfirm}
          onConfirm={handleConfirm}
        />
      </Stack>
    </>
  );
}
